import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectDate,
  selectSlot,
  selectETA,
  selectScheduled,
  fetchAvailability
} from './actions';
import classnames from 'classnames';
import RadioOption from './RadioOption';
import DeliveryTimePicker from './DeliveryTimePicker';
import DeliveryTimeDropdowns from './DeliveryTimeDropdowns';
import { Body } from 'scy/text';
import { sansSerifRegular, success } from 'scy/text/styles.mod.scss';
import Header from './Header';
import Nav from './WizardNav';
import ProgressIndicator from './ProgressIndicator';
import Main from './WizardMain';
import LoadingSpinner from 'scy/LoadingSpinner';
import { Warning } from 'scy/Warning';
import { hidden, tablet_block, tablet_hidden } from 'scy/display.mod.scss';
import { relative } from 'scy/padding.mod.scss';
import margin from 'scy/margin.mod.scss';
import border from 'scy/border.mod.scss';
import padding from 'scy/padding.mod.scss';

export default function When({ prevPath, nextPath }) {
  const dispatch = useDispatch();
  const state = useSelector(state => state.giftClaim);
  const {
    availabilityLoading,
    availabilityError,
    canDeliverASAP,
    canScheduleLater,
    etaDisplay,
    etaSelectionText,
    dateOptions,
    date,
    slot,
    selectedETA
  } = state;
  const order = state.order || {};
  const customer = order.customer || {};
  const isASAP = !!selectedETA;

  const selectASAP = React.useCallback(() => {
    if (etaDisplay) dispatch(selectETA(etaDisplay));
  }, [dispatch, etaDisplay]);

  useEffect(() => {
    dispatch(fetchAvailability());
  }, [dispatch]);

  useEffect(() => {
    if (!date && !slot) selectASAP();
  }, [date, selectASAP, slot]);

  return (
    <>
      <Header />

      <Nav prevPath={prevPath}>
        <ProgressIndicator steps={{ when: 'active', where: 'completed' }} />
      </Nav>

      <Main
        customer={customer}
        title="When would you like it delivered?"
        nextPath={isASAP || (!!date && !!slot) ? nextPath : null}
        buttonText="Set and Continue"
      >
        {availabilityLoading ? (
          <Body className={classnames(padding.xl, relative)}>
            <LoadingSpinner orange />
          </Body>
        ) : availabilityError ? (
          <Warning className={margin.md}>{availabilityError}</Warning>
        ) : (
          <>
            <div className={tablet_hidden}>
              {canDeliverASAP && (
                <div className={classnames(border.top, border.bottom)}>
                  <RadioOption
                    id="deliverASAP"
                    name="deliveryTime"
                    checked={!!isASAP}
                    onChange={selectASAP}
                  >
                    <span>
                      Deliver ASAP
                      {!!etaDisplay && (
                        <>
                          {' '}
                          <span
                            className={classnames(sansSerifRegular, success)}
                          >
                            (Est. {etaDisplay.replace(/minutes/i, 'min')})
                          </span>
                        </>
                      )}
                    </span>
                  </RadioOption>
                </div>
              )}

              {canScheduleLater && (
                <div
                  className={classnames(margin.bottom_xl, border.bottom, {
                    [border.top]: !canDeliverASAP
                  })}
                >
                  <RadioOption
                    id="scheduleDelivery"
                    name="deliveryTime"
                    checked={!isASAP}
                    onChange={() => dispatch(selectScheduled())}
                  >
                    Schedule for Later
                  </RadioOption>

                  {!isASAP && (
                    <DeliveryTimeDropdowns
                      options={dateOptions.filter(
                        option => option.slots && option.slots.length
                      )}
                      date={date}
                      slot={slot}
                      selectDate={date => dispatch(selectDate(date))}
                      selectSlot={slot => dispatch(selectSlot(slot))}
                    />
                  )}
                </div>
              )}
            </div>

            <div className={classnames(hidden, tablet_block)}>
              <DeliveryTimePicker
                options={canScheduleLater ? dateOptions : []}
                date={date}
                slot={slot}
                isASAP={isASAP}
                labelForASAP={canDeliverASAP ? etaSelectionText : null}
                selectASAP={selectASAP}
                selectDate={date => dispatch(selectDate(date))}
                selectSlot={slot => dispatch(selectSlot(slot))}
              />
            </div>
          </>
        )}
      </Main>
    </>
  );
}
